/**
 * A transaction's fee % and its Apply fees checkbox as every screen shows
 * them (IP-203; design docs/superpowers/specs/2026-10-19-apply-fees-checkbox-design.md
 * section 3.5). Nothing here decides a fee: the server sends its decision
 * (the domain's approval/fees.js) and this module only puts it into words.
 *
 * Two shapes reach the page:
 *
 *   order.fees        an order (GET /orders, the order detail) - feeTermsOf:
 *                     { kind, applicable, waived, applied, rate, fundRate,
 *                       provisional, reason, holdingMonths, byDefault,
 *                       default }
 *                     reason says why the box is off and disabled:
 *                     NO_ENTRY_FEE, NO_EXIT_FEE or TRANSFER. IP-204:
 *                     byDefault - nobody has chosen, so the fund's rule set
 *                     the box; default - that rule's comparison (threshold,
 *                     amount, units, valuedAt, priceDate, provisional) when a
 *                     condition is switched on
 *   txn.feeRate       a posted register transaction (GET register/
 *                     transactions, the holder's history, the Excel export) -
 *                     postedFeeRateOf: { kind, rate, fundRate, applied,
 *                     overridden, holdingMonths }; overridden marks an older
 *                     subscription charged its own rate set at review (IP-139)
 *
 * feeOf reads either into one display shape, so a cell never has to know
 * which it was given.
 */

import { lkrText, percentText } from '@lfoc/domain/fees/rules.js';

/** A rate as a percentage: a fraction (0.015000) as '1.5%'; null for none. In decimals, never a float (fees/rules.js percentText). */
export function ratePercent(rate) {
    if (rate === null || rate === undefined) return null;
    return percentText(String(rate)) + '%';
}

/**
 * The fee to show for a row - an order's terms or a posting's rate - or null
 * when no fee % applies to it at all (a transfer, an upload, a leg whose fund
 * charges no such fee).
 *
 * @param {{ fees?: object|null, feeRate?: object|null }} row
 * @returns {{ kind: 'ENTRY'|'EXIT', rate: string|null, fundRate: string|null, applied: boolean, overridden: boolean,
 *   provisional: boolean, holdingMonths: number|null } | null}
 */
export function feeOf(row) {
    const fees = row?.fees ?? null;
    if (fees) {
        // An order: nothing to show where the fund charges no such fee (the box says why instead).
        if (!fees.kind || !fees.applicable) return null;
        return {
            kind: fees.kind, rate: fees.rate ?? null, fundRate: fees.fundRate ?? null, applied: fees.applied === true, overridden: false,
            provisional: fees.provisional === true, holdingMonths: fees.holdingMonths ?? null,
            // IP-204: the fund's rule set the box (nobody chose yet), and the amount it compared with.
            ruleThreshold: fees.byDefault && fees.default?.threshold ? fees.default.threshold : null
        };
    }
    const posted = row?.feeRate ?? null;
    if (!posted?.kind) return null;
    return {
        kind: posted.kind, rate: posted.rate ?? null, fundRate: posted.fundRate ?? posted.rate ?? null, applied: posted.applied !== false,
        overridden: posted.overridden === true, provisional: false, holdingMonths: posted.holdingMonths ?? null
    };
}

/**
 * The words beside the fee: the % and, when it matters, where it comes from.
 *
 *   '1.12%'                                    a subscription at the fund's rate
 *   '1.01% on units held under 12 months'      a redemption: only its young units pay it
 *   'Fees not applied - fund 1.12%'            Apply fees unticked at review
 *   'Fees not applied (fund rule: not more than LKR 100,000.00) - fund 1.12%'
 *                                              IP-204: unticked by the fund's rule, nobody has chosen yet
 *   '1.12% (provisional)'                      before the dealing date's price is published
 *   '0.75%, set at review - fund 1.12%'        an older subscription's own rate (IP-139)
 *
 * With ofAmount, a subscription's rate says what it is charged on, right after it:
 * '1.12% of the amount'.
 *
 * @param {object|null} fee feeOf's answer (an older caller may pass a posting's feeRate directly)
 * @param {{ ofAmount?: boolean }} [options]
 * @returns {string|null} null when there is no rate to show
 */
export function feeRateText(fee, { ofAmount = false } = {}) {
    if (!fee) return null;
    if (fee.applied === false) {
        // Unticked at review: say so, with the fund's % the transaction was spared.
        const fund = ratePercent(fee.fundRate ?? null);
        const rule = fee.ruleThreshold ? ' (fund rule: not more than ' + lkrText(fee.ruleThreshold) + ')' : '';
        return 'Fees not applied' + rule + (fund ? ' - fund ' + fund : '');
    }
    const rate = ratePercent(fee.rate ?? null);
    if (!rate) return null;
    let text = rate;
    if (fee.kind === 'EXIT') {
        // The exit fee is charged on the young units alone (IP-98), so say which.
        if (fee.holdingMonths) text += ' on units held under ' + fee.holdingMonths + ' months';
    } else if (ofAmount) {
        text += ' of the amount';
    }
    if (fee.overridden) text += ', set at review - fund ' + (ratePercent(fee.fundRate ?? null) ?? '0%');
    if (fee.provisional) text += ' (provisional)';
    return text;
}

/** What each type of transaction is called in the tooltip's sentence. */
const NOUN = Object.freeze({
    SUB: 'subscription', SWI_IN: 'switch-in', RED: 'redemption', SWI_OUT: 'switch-out', TRF_OUT: 'transfer', TRF_IN: 'transfer'
});

/** A YYYY-MM-DD date as the tooltip reads it: 21 Oct 2026. */
function dayText(date) {
    if (!date) return null;
    const [y, m, d] = String(date).slice(0, 10).split('-').map(Number);
    const month = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][m - 1];
    return month ? d + ' ' + month + ' ' + y : String(date);
}

/**
 * The Apply fees checkbox of one leg, as the Review dialog draws it: whether
 * it may be changed, whether it is ticked, and the tooltip - why it is off
 * and disabled, or what unticking it does.
 *
 * @param {{ orderType: string, fundId?: string, dealingDate?: string|null, fees?: object|null }} order
 * @returns {{ enabled: boolean, checked: boolean, tooltip: string }}
 */
export function applyFeesBox(order) {
    const fees = order?.fees ?? null;
    const noun = NOUN[order?.orderType] ?? 'transaction';
    const on = dayText(order?.dealingDate) ? ' on ' + dayText(order.dealingDate) : '';
    const fund = order?.fundId ?? 'The fund';
    if (!fees || fees.reason === 'TRANSFER' || (!fees.kind && !fees.reason)) {
        return { enabled: false, checked: false, tooltip: 'A transfer moves units at cost between holders. No fee applies.' };
    }
    if (fees.reason === 'NO_ENTRY_FEE') {
        return {
            enabled: false, checked: false,
            tooltip: fund + ' charges no front-end fee' + on + ', so this ' + noun + ' is priced at the selling price without fees.'
        };
    }
    if (fees.reason === 'NO_EXIT_FEE') {
        return {
            enabled: false, checked: false,
            tooltip: fund + ' charges no exit fee' + on + ', so this ' + noun + ' is priced at the buying price without fees.'
        };
    }
    const rate = ratePercent(fees.fundRate ?? null);
    const pct = rate ? ' ' + rate : '';
    let tooltip = fees.kind === 'EXIT'
        ? 'The' + pct + ' exit fee applies only to units held less than ' + (fees.holdingMonths ?? 12)
            + ' months. Untick to price them at the buying price without it.'
        : 'Untick to price this ' + noun + ' at the selling price without the' + pct + ' front-end fee.';
    if (fees.provisional) tooltip += ' Provisional: the rate in force' + on + '; the published price confirms it.';
    const rule = ruleText(fund, fees, pct);
    if (rule) tooltip = rule + ' ' + tooltip;
    // IP-204: the box as the server decided it - the leg's own choice, or the fund's rule while nobody has chosen.
    return { enabled: true, checked: fees.applied === true, tooltip };
}

/** A unit count as the tooltip reads it: '5000.0000' -> '5,000', '12.5000' -> '12.5'. */
function unitsText(units) {
    const [whole, part = ''] = String(units).split('.');
    const decimals = part.replace(/0+$/, '');
    return whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',') + (decimals ? '.' + decimals : '');
}

/**
 * IP-204: why the fund's rule set the box, when it did - nobody has chosen
 * yet (byDefault) and the fund has a condition switched on. Null otherwise:
 * a person's choice needs no explaining, and a fund with no condition ticks
 * every box, as before.
 *
 *   'Ticked by UTDOF's rule: LKR 1,000,000.00 is more than LKR 450,000.00.'
 *   'Unticked by UTDOF's rule: 2,000 units x 200.0000 (the price of 2 Oct 2028) = LKR 400,000.00, not more than
 *    LKR 450,000.00. Tick to charge the 1% exit fee.'
 */
function ruleText(fund, fees, pct) {
    const rule = fees.byDefault ? fees.default : null;
    if (!rule?.threshold) return null;
    const limit = lkrText(rule.threshold);
    if (!rule.amount) {
        return 'Ticked: ' + fund + ' ticks this box only above ' + limit + ', and this transaction cannot be valued until '
            + fund + ' publishes a price.';
    }
    const valued = rule.valuedAt
        ? unitsText(rule.units) + ' units x ' + rule.valuedAt + (rule.priceDate ? ' (the price of ' + dayText(rule.priceDate) + ')' : '') + ' = '
        : '';
    const later = rule.provisional ? ' The dealing date\'s price may change this until the transaction is reviewed.' : '';
    if (rule.applies) return 'Ticked by ' + fund + '\'s rule: ' + valued + lkrText(rule.amount) + ', more than ' + limit + '.' + later;
    const fee = fees.kind === 'EXIT' ? 'exit fee' : 'front-end fee';
    return 'Unticked by ' + fund + '\'s rule: ' + valued + lkrText(rule.amount) + ', not more than ' + limit + '. Tick to charge the'
        + pct + ' ' + fee + '.' + later;
}
